<script lang="ts">
  import type { VideoThumbnail } from '@foundation-player/loader';

  /*
   * Thumbnail images come as a sprite, that is one image that contains multiple
   * thumbnails. The information we get is the image url, the size of the
   * thumbnail and the position within the sprite.
   *
   * We show the thumbnail using the background window pattern: We set the size
   * of the thumbnail to the image div, set the background image and position it
   * according to the thumbnail position within the sprite.
   *
   * Now the background window pattern has a caveat: The image div needs to have
   * the size of the thumbnail. We cannot scale it using the background-size
   * property, because we would have to know the number of thumbnails in the
   * sprite.
   *
   * So we scale the thumbnails using the `transform: scale()` property. We
   * scale down (or up) the image according to the `width` prop and the
   * thumbnail width.
   *
   * Now the `transform` property does not change the size of the image div,
   * only the presentation. So we add a wrapper with the requested size and
   * hidden overflow.
   */

  /** The metadata for the thumbnail */
  export let image: VideoThumbnail | undefined;

  /** The display width of the thumbnail in pixels. If omitted, the width of the
   * thumbnail image is used. */
  export let width: number | undefined = undefined;

  /** The actual display width */
  let thumbnailWidth = 320;

  /** The actual display height */
  let thumbnailHeight = 180;

  /** The scale factor, calculated from the thumbnail width and the display
   * width */
  let scale = 1;

  $: {
    if (image) {
      const aspectRatio = image.coords.w / image.coords.h;
      thumbnailWidth = width || image.coords.w || 320;
      thumbnailHeight = thumbnailWidth / aspectRatio;
      scale = thumbnailWidth / image.coords.w;
    }
  }
</script>

{#if image}
  <div class="wrapper" style:width="{thumbnailWidth}px" style:height="{thumbnailHeight}px">
    <div
      class="image"
      data-testid="thumbnail"
      style:background-image="url({image.url})"
      style:background-position-x="-{image.coords.x}px"
      style:background-position-y="-{image.coords.y}px"
      style:width="{image.coords.w}px"
      style:height="{image.coords.h}px"
      style:transform="scale({scale})"
    />
  </div>
{/if}

<style module>
  .wrapper {
    overflow: hidden;
  }

  .image {
    transform-origin: top left;
  }
</style>
